/**
 * @fileoverview Drawer lateral do carrinho de compras com integração WhatsApp.
 * Permite gerenciar quantidades, remover itens e finalizar pedido via WhatsApp
 * com informações do cliente e endereço de entrega.
 * 
 * @module CartDrawer
 * @requires react-redux
 * @requires @mui/material
 * @since 1.0.0
 */

import { useSelector, useDispatch } from "react-redux";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Button
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { updateCartQuantity, removeFromCart } from "../../../store/slices/cartSlice";
import { formatPrice, calculateCartTotal, calculateSubtotal } from "../../../utils/priceUtils";

/**
 * Drawer lateral do carrinho de compras com integração WhatsApp.
 * Layout responsivo: tela cheia em mobile, 400px em desktop.
 * 
 * Funcionalidades:
 * - Exibir itens do carrinho com imagem, nome, preço e quantidade
 * - Ajustar quantidade de itens (incrementar/decrementar)
 * - Remover itens do carrinho
 * - Calcular total do carrinho
 * - Enviar pedido via WhatsApp com dados do cliente e endereço
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.open - Se o drawer está visível
 * @param {Function} props.onClose - Callback para fechar o drawer
 * @returns {JSX.Element} Drawer com lista de produtos e ações do carrinho
 * 
 * @example
 * <CartDrawer open={isOpen} onClose={() => setIsOpen(false)} />
 */
function CartDrawer({ open, onClose }) {
  const dispatch = useDispatch();
  const { items: cart } = useSelector((state) => state.cart);
  const user = useSelector((state) => state.auth.user);
  
  const cartTotal = calculateCartTotal(cart);

  /**
   * Gera e envia pedido via WhatsApp.
   * Formata mensagem com:
   * - Nome do cliente (em negrito)
   * - Endereço de entrega completo (rua, logradouro, número, complemento, cidade, estado, CEP)
   * - Lista de itens com detalhes (nome, marca, quantidade, preços)
   * - Total geral
   * 
   * @function
   * @returns {void}
   */
  const handleWhatsAppOrder = () => {
    if (cart.length === 0) return;
    
    let message = "*Pedido VikanaWeb*\n\n";
    
    // Adiciona nome do cliente em negrito
    if (user?.nome) {
      message += `*Cliente: ${user.nome}*\n\n`;
    }
    
    // Adiciona endereço de entrega com emoji vermelho
    if (user?.rua || user?.logradouro || user?.cidade) {
      message += `🔴 *Endereço de Entrega:*\n`;
      if (user.rua) message += `${user.rua}\n`;
      if (user.logradouro) message += `${user.logradouro}`;
      if (user.numero) message += `, ${user.numero}`;
      if (user.logradouro || user.numero) message += `\n`;
      if (user.complemento) message += `${user.complemento}\n`;
      if (user.cidade) message += `${user.cidade}`;
      if (user.estado) message += ` - ${user.estado}`;
      if (user.cidade || user.estado) message += `\n`;
      if (user.cep) message += `CEP: ${user.cep}\n`;
      message += `\n`;
    }
    
    message += `*Itens do Pedido:*\n\n`;
    
    // forEach: monta mensagem do pedido para WhatsApp
    cart.forEach((item, index) => {
      message += `${index + 1}. ${item.nome}\n`;
      message += `   Marca: ${item.marca}\n`;
      message += `   Quantidade: ${item.quantity}\n`;
      message += `   Valor unitário: ${formatPrice(item.preco)}\n`;
      message += `   Subtotal: ${formatPrice(calculateSubtotal(item.preco, item.quantity))}\n\n`;
    });
    
    message += `*Total Geral: ${formatPrice(cartTotal)}*`;
    
    const phoneNumber = "+5515981027355";
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1500,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          maxWidth: "100vw"
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 1 }}>
          <Typography variant="h6">
            Carrinho de Compras
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
        <Divider />
        
        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          {cart.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Seu carrinho está vazio
            </Typography>
          ) : (
            <>
              <List>
                {cart.map((item) => (
                  <ListItem key={item.id} sx={{ px: 0, py: 1 }}>
                    <ListItemAvatar>
                      <Box
                        component="img"
                        src={item.imagem || "/product_without_image.png"}
                        alt={item.nome}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: "contain",
                          borderRadius: 1
                        }}
                      />
                    </ListItemAvatar>
                    <Box sx={{ flex: 1, ml: 2 }}>
                      <Typography variant="subtitle2" sx={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        {item.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice(item.preco)}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => dispatch(updateCartQuantity({ id: item.id, quantity: item.quantity - 1 }))}
                        >
                          <RemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ mx: 1, minWidth: 20, textAlign: "center" }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => dispatch(updateCartQuantity({ id: item.id, quantity: item.quantity + 1 }))}
                        >
                          <AddOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => dispatch(removeFromCart(item.id))}
                          sx={{ ml: 1, color: "error.main" }}
                        >
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                        Total: {formatPrice(calculateSubtotal(item.preco, item.quantity))}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">
                  Total Geral:
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(cartTotal)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            variant="contained"
            color="success"
            onClick={handleWhatsAppOrder}
            disabled={cart.length === 0}
            startIcon={<WhatsAppIcon />}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Comprar via WhatsApp
          </Button>
          <Button 
            variant="contained" 
            onClick={onClose}
            sx={{ width: '25%' }}
          >
            Fechar
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default CartDrawer;